import { Link, NavLink, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import { Menu, X, ChevronDown, Facebook, PhoneCall, Globe, Home as HomeIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import LanguageSwitcher from "@/components/LanguageSwitcher";

const navItems = [
  { to: "/", label: "Home", icon: "home" },
  {
    label: "Visa Services",
    children: [
      {
        label: "Study, Training & Graduate Visas",
        group: [
          { to: "/services/student", label: "Student Visa (Subclass 500)" },
          { to: "/services/student-guardian-590", label: "Student Guardian Visa (Subclass 590)" },
          { to: "/services/training-407", label: "Training Visa (Subclass 407)" },
          { to: "/services/graduate", label: "Temporary Graduate Visa (Subclass 485)" },
        ],
      },
      {
        label: "Employer Sponsored & Work Visas",
        group: [
          { to: "/services/skills-in-demand-482", label: "Skills in Demand Visa (Subclass 482)" },
          { to: "/services/employer-nomination-186", label: "Employer Nomination Scheme (Subclass 186)" },
          { to: "/services/standard-business-sponsorship", label: "Standard Business Sponsorship (SBS)" },
          { to: "/services/labour-agreements", label: "Labour Agreements" },
        ],
      },
      {
        label: "Skilled Migration Visas",
        group: [
          { to: "/services/skilled-independent-189", label: "Skilled Independent Visa (Subclass 189)" },
          { to: "/services/skilled-nominated-190", label: "Skilled Nominated Visa (Subclass 190)" },
          { to: "/services/skilled-work-regional-491", label: "Skilled Work Regional Visa (Subclass 491)" },
          { to: "/services/skilled-regional-pr-191", label: "Permanent Residence (Skilled Regional) (Subclass 191)" },
          { to: "/services/national-innovation-visa", label: "National Innovation Visa (Subclass 858)" },
        ],
      },
      {
        label: "Visitor, Travel & Medical Visas",
        group: [
          { to: "/services/visitor", label: "Visitor Visa (Subclass 600)" },
          { to: "/services/visitor-visa-subclass-600", label: "Tourist & Family Sponsored Stream" },
        ],
      },
      {
        label: "Parent & Partner Visas",
        group: [
          { to: "/partner-visa", label: "Partner Visa (Subclass 309 / 100 · 820 / 801)" },
          { to: "/parent-visas", label: "Parent Visas (103 · 143 · 173 · 804 · 864 · 884 · 870)" },
          { to: "/parent-visa-finder", label: "Parent Visa Pathway Assessment" },
        ],
      },
      {
        label: "Business & Investment Visas",
        group: [
          { to: "/services/business", label: "Business & Investor Pathways" },
          { to: "/services/national-innovation-visa", label: "National Innovation Visa (Subclass 858)" },
          { to: "/services/labour-agreements", label: "Labour Agreements" },
          { to: "/services/dama-visas", label: "DAMA Pathways" },
          { to: "/services/standard-business-sponsorship", label: "Standard Business Sponsorship (SBS)" },
        ],
      },
      {
        label: "Citizenship",
        group: [
          { to: "/services/citizenship", label: "Citizenship by Conferral & Descent" },
          { to: "/services/citizenship-application", label: "Application Preparation & Test Support" },
        ],
      },
      {
        label: "Visa Refusal & Appeals",
        group: [
          { to: "/services/appeals", label: "ART Review & Refusal Strategy" },
        ],
      },
      {
        label: "Other Visas",
        group: [
          { to: "/services/national-innovation-visa", label: "National Innovation Visa (Subclass 858)" },
          { to: "/services/dama-visas", label: "DAMA — Designated Area Migration Agreements" },
        ],
      },
    ],
  },
  {
    label: "Tools & Assessments",
    children: [
      { to: "/pr-assessment", label: "PR Assessment" },
      { to: "/points-calculator", label: "Points Test Calculator" },
      { to: "/student-fund-calculator", label: "Student Fund Calculator" },
      { to: "/document-checklist", label: "Document Checklist Tool" },
      { to: "/skilled-migration-assessment", label: "Skilled Migration Pathway Assessment" },
      { to: "/parent-visa-finder", label: "Parent Visa Pathway Assessment" },
      { to: "/485-pathway-planner", label: "485 Pathway Planner — Eligibility & Risk" },
      { to: "/visa-statement-builder", label: "GS & SOP Statement Builder" },
      { to: "https://studynet.ai/", external: true, label: "Find Courses, Scholarships & Study Pathways" },
      { to: "/service-charges", label: "Service Charges" },
      { to: "/resources", label: "Latest Updates" },
    ],
  },
  { to: "/book-consultation", label: "Book Consultation", isCta: true },
];

const SOCIAL = [
  { Icon: Facebook, href: "https://www.facebook.com/profile.php?id=61589771857650", label: "Facebook" },
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [mobileSection, setMobileSection] = useState(null);
  const location = useLocation();

  useEffect(() => {
    setOpen(false);
    setOpenDropdown(null);
    setMobileSection(null);
  }, [location.pathname]);

  return (
    <header
      data-testid="site-header"
      className="sticky top-0 z-50 bg-white/90 backdrop-blur-xl border-b border-slate-200/80 shadow-[0_1px_0_0_rgba(90,58,139,0.04)]"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="h-20 sm:h-24 lg:h-28 flex items-center justify-between gap-2 sm:gap-3 lg:gap-6">
          <Link to="/" data-testid="header-logo-link" className="flex items-center shrink-0 group">
            <div className="flex flex-col leading-none">
              <span className="flex items-baseline gap-2 sm:gap-2.5 whitespace-nowrap">
                <span className="font-display font-bold text-[21px] sm:text-[27px] lg:text-[31px] text-[#29324A] tracking-[0.03em] group-hover:text-[#5A3A8B] transition-colors" style={{ fontVariant: "small-caps" }}>
                  S. Yasmin
                </span>
                <span className="font-script text-[26px] sm:text-[34px] lg:text-[40px] text-[#C43A89] leading-none -translate-y-[2px]">
                  Anwar
                </span>
              </span>
              <span className="flex flex-col mt-1 gap-[3px]">
                <span className="text-[7px] sm:text-[8px] lg:text-[8.5px] font-bold tracking-[0.14em] text-[#5A3A8B] uppercase whitespace-nowrap">
                  Director, StudyNet Pty Ltd
                </span>
                <span className="text-[7px] sm:text-[8px] lg:text-[8.5px] font-semibold tracking-[0.1em] text-slate-500 uppercase whitespace-nowrap">
                  Registered Migration Agent, Australia | MARN 2619415
                </span>
                <span className="text-[7px] sm:text-[8px] lg:text-[8.5px] font-semibold tracking-[0.1em] text-slate-500 uppercase whitespace-nowrap">
                  Licensed Immigration Adviser, New Zealand | Licence No. 202603837
                </span>
              </span>
            </div>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-1 ml-8 xl:ml-12">
            {navItems.filter((i) => !i.isCta).map((item) =>
              item.children ? (
                <div
                  key={item.label}
                  className="relative"
                  onMouseEnter={() => setOpenDropdown(item.label)}
                  onMouseLeave={() => setOpenDropdown(null)}
                >
                  <button
                    data-testid={`nav-${item.label.replace(/\s+/g, "-").toLowerCase()}-trigger`}
                    className="px-3 py-2 text-sm font-medium text-[#29324A] hover:text-[#5A3A8B] flex items-center gap-1 transition-colors whitespace-nowrap"
                  >
                    {item.label}
                    <ChevronDown className="h-3 w-3" />
                  </button>
                  {openDropdown === item.label && (
                    <div className="absolute top-full left-0 pt-2 w-80">
                      <div className="bg-white border border-slate-200 shadow-lg py-2 max-h-[70vh] overflow-y-auto">
                        {item.children.map((c) =>
                          c.group ? (
                            <div key={c.label} className="px-2 pt-3 pb-1.5 border-t border-slate-100 first:border-0">
                              <div className="px-2 text-[10px] font-bold tracking-[0.18em] text-[#C43A89] uppercase mb-1">
                                {c.label}
                              </div>
                              {c.group.map((g) => (
                                g.external ? (
                                  <a
                                    key={g.to + g.label}
                                    href={g.to}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-testid={`nav-ext-${g.label.toLowerCase().replace(/[^a-z0-9]+/g, "-").slice(0,40)}`}
                                    className="flex items-center justify-between gap-2 px-3 py-2 text-[13px] text-[#29324A] hover:bg-gradient-to-r hover:from-[#29324A] hover:to-[#5A3A8B] hover:text-white transition-colors"
                                  >
                                    <span>{g.label}</span>
                                    <span aria-hidden className="text-[10px] tracking-wider uppercase text-[#C43A89] font-bold">↗</span>
                                  </a>
                                ) : (
                                  <NavLink
                                    key={g.to}
                                    to={g.to}
                                    data-testid={`nav-link-${g.to.replace(/\//g, "-")}`}
                                    className={({ isActive }) =>
                                      `block px-3 py-2 text-[13px] transition-colors ${
                                        isActive ? "text-[#29324A] bg-[#F8F4FB]" : "text-[#29324A] hover:bg-gradient-to-r hover:from-[#29324A] hover:to-[#5A3A8B] hover:text-white"
                                      }`
                                    }
                                  >
                                    {g.label}
                                  </NavLink>
                                )
                              ))}
                            </div>
                          ) : (
                            c.external ? (
                              <a
                                key={c.to + c.label}
                                href={c.to}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid={`nav-ext-${c.label.toLowerCase().replace(/[^a-z0-9]+/g, "-").slice(0,40)}`}
                                className="flex items-center justify-between gap-2 px-4 py-2 text-sm text-[#29324A] hover:bg-gradient-to-r hover:from-[#29324A] hover:to-[#5A3A8B] hover:text-white transition-colors"
                              >
                                <span>{c.label}</span>
                                <span aria-hidden className="text-[10px] tracking-wider uppercase text-[#C43A89] font-bold">↗</span>
                              </a>
                            ) : (
                              <NavLink
                                key={c.to + c.label}
                                to={c.to}
                                data-testid={`nav-link-${c.to.replace(/\//g, "-")}`}
                                className={({ isActive }) =>
                                  `block px-4 py-2 text-sm transition-colors ${
                                    isActive ? "text-[#29324A] bg-[#F8F4FB]" : "text-[#29324A] hover:bg-gradient-to-r hover:from-[#29324A] hover:to-[#5A3A8B] hover:text-white"
                                  }`
                                }
                              >
                                {c.label}
                              </NavLink>
                            )
                          )
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ) : item.icon === "home" ? (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end
                  data-testid={`nav-link-${item.to.replace(/\//g, "-")}`}
                  aria-label="Home"
                  title="Home"
                  className={({ isActive }) =>
                    `p-2 transition-colors ${
                      isActive ? "text-[#5A3A8B]" : "text-[#29324A] hover:text-[#5A3A8B]"
                    }`
                  }
                >
                  <HomeIcon className="h-4.5 w-4.5" />
                </NavLink>
              ) : (
                <NavLink
                  key={item.to}
                  to={item.to}
                  data-testid={`nav-link-${item.to.replace(/\//g, "-")}`}
                  className={({ isActive }) =>
                    `px-3 py-2 text-sm font-medium transition-colors whitespace-nowrap ${
                      isActive ? "text-[#5A3A8B]" : "text-[#29324A] hover:text-[#5A3A8B]"
                    }`
                  }
                >
                  {item.label}
                </NavLink>
              )
            )}
          </nav>

          {/* Desktop right side */}
          <div className="hidden lg:flex items-center gap-3 shrink-0">
            <LanguageSwitcher />
            <Link to="/book-consultation" data-testid="header-cta-book">
              <Button className="bg-gradient-to-r from-[#C43A89] to-[#a22b6e] hover:from-[#a22b6e] hover:to-[#7a1f51] text-white rounded-none font-semibold tracking-wider uppercase text-xs px-5 py-2.5 btn-shimmer shadow-md shadow-[#C43A89]/20">
                Book Consultation
              </Button>
            </Link>
          </div>

          {/* Mobile right side */}
          <div className="lg:hidden flex items-center gap-1 shrink-0">
            <LanguageSwitcher mobile />
            <button
              data-testid="mobile-menu-toggle"
              className="p-2 text-[#29324A]"
              onClick={() => setOpen(!open)}
              aria-label="Toggle menu"
            >
              {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu — collapsible sections */}
      {open && (
        <div data-testid="mobile-menu" className="lg:hidden border-t border-slate-200 bg-white max-h-[80vh] overflow-y-auto">
          <div className="px-4 py-3">
            {navItems.map((item) =>
              item.children ? (
                <div key={item.label} className="border-b border-slate-100 last:border-0">
                  <button
                    type="button"
                    data-testid={`mobile-section-${item.label.replace(/\s+/g, "-").toLowerCase()}`}
                    onClick={() => setMobileSection(mobileSection === item.label ? null : item.label)}
                    aria-expanded={mobileSection === item.label}
                    className="w-full flex items-center justify-between px-2 py-3.5 text-sm font-semibold text-[#29324A]"
                  >
                    {item.label}
                    <ChevronDown
                      className={`h-4 w-4 text-[#29324A] transition-transform ${
                        mobileSection === item.label ? "rotate-180" : ""
                      }`}
                    />
                  </button>
                  {mobileSection === item.label && (
                    <div className="pb-3 pl-2 space-y-0.5 animate-in fade-in slide-in-from-top-1">
                      {item.children.map((c) =>
                        c.group ? (
                          <div key={c.label} className="pt-2 pb-1">
                            <div className="px-3 text-[10px] font-bold tracking-[0.18em] text-[#C43A89] uppercase mb-1">
                              {c.label}
                            </div>
                            {c.group.map((g) => (
                              g.external ? (
                                <a
                                  key={g.to + g.label}
                                  href={g.to}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="flex items-center justify-between gap-2 px-3 py-2.5 text-[13px] text-slate-700 hover:bg-gradient-to-r hover:from-[#29324A] hover:to-[#5A3A8B] hover:text-white transition-colors"
                                >
                                  <span>{g.label}</span>
                                  <span aria-hidden className="text-[10px] text-[#C43A89] font-bold">↗</span>
                                </a>
                              ) : (
                                <NavLink
                                  key={g.to}
                                  to={g.to}
                                  className={({ isActive }) =>
                                    `block px-3 py-2.5 text-[13px] transition-colors ${
                                      isActive ? "text-[#29324A] bg-[#F8F4FB]" : "text-slate-700 hover:bg-gradient-to-r hover:from-[#29324A] hover:to-[#5A3A8B] hover:text-white"
                                    }`
                                  }
                                >
                                  {g.label}
                                </NavLink>
                              )
                            ))}
                          </div>
                        ) : (
                          c.external ? (
                            <a
                              key={c.to + c.label}
                              href={c.to}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center justify-between gap-2 px-3 py-2.5 text-sm rounded-sm text-slate-700 hover:bg-gradient-to-r hover:from-[#29324A] hover:to-[#5A3A8B] hover:text-white transition-colors"
                            >
                              <span>{c.label}</span>
                              <span aria-hidden className="text-[10px] text-[#C43A89] font-bold">↗</span>
                            </a>
                          ) : (
                            <NavLink
                              key={c.to + c.label}
                              to={c.to}
                              data-testid={`mobile-nav-link-${c.to.replace(/\//g, "-")}`}
                              className={({ isActive }) =>
                                `block px-3 py-2.5 text-sm rounded-sm transition-colors ${
                                  isActive ? "text-[#29324A] bg-[#F8F4FB]" : "text-slate-700 hover:bg-gradient-to-r hover:from-[#29324A] hover:to-[#5A3A8B] hover:text-white"
                                }`
                              }
                            >
                              {c.label}
                            </NavLink>
                          )
                        )
                      )}
                    </div>
                  )}
                </div>
              ) : item.isCta ? (
                <Link key={item.to} to={item.to} className="block pt-4">
                  <Button
                    data-testid="mobile-cta-book"
                    className="w-full bg-gradient-to-r from-[#C43A89] to-[#a22b6e] hover:from-[#a22b6e] hover:to-[#7a1f51] text-white rounded-none font-semibold tracking-wider uppercase text-xs py-3"
                  >
                    {item.label}
                  </Button>
                </Link>
              ) : (
                <NavLink
                  key={item.to}
                  to={item.to}
                  data-testid={`mobile-nav-link-${item.to.replace(/\//g, "-")}`}
                  className={({ isActive }) =>
                    `block px-2 py-3.5 text-sm font-semibold border-b border-slate-100 last:border-0 transition-colors ${
                      isActive ? "text-[#29324A]" : "text-[#29324A] hover:text-[#29324A]"
                    }`
                  }
                >
                  {item.label}
                </NavLink>
              )
            )}

            {/* Socials */}
            <div className="pt-5 flex items-center justify-center gap-2 border-t border-slate-100 mt-2">
              {SOCIAL.map(({ Icon, href, label }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={label}
                  className="h-10 w-10 flex items-center justify-center border border-slate-200 text-slate-600 hover:text-[#C43A89] hover:border-[#C43A89] transition-colors"
                >
                  <Icon className="h-4 w-4" />
                </a>
              ))}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
